/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { of, map } from 'rxjs'
import { sha256 } from 'js-sha256'
import { isEqual } from 'lodash'

import type { IAnalyticsClient } from '@elastic/ebt/client/src/analytics_client'

import { getBillingThirdPartyIntegrationContext } from '@modules/billing-lib/billingDetails/utils'
import type { BillingDetails } from '@modules/billing-api/customTypes'

import { getSessionId } from './utils'

import type { Observable } from 'rxjs'

export class EbtAnalytics {
  private analytics: IAnalyticsClient

  private currentFlags: Record<string, any> | null = null

  constructor(analytics: IAnalyticsClient) {
    this.analytics = analytics
    this.registerSessionIdContext()
    this.registerBrowserInfoAnalyticsContext()
  }

  private registerSessionIdContext() {
    try {
      this.analytics.registerContextProvider({
        name: 'session-id',
        context$: of({ session_id: getSessionId() }),
        schema: {
          session_id: {
            type: 'keyword',
            _meta: { description: 'Unique session ID for every browser session' },
          },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering session ID context', e)
    }
  }

  private registerBrowserInfoAnalyticsContext() {
    try {
      this.analytics.registerContextProvider({
        name: 'browser info',
        context$: of({
          user_agent: navigator.userAgent,
          preferred_language: navigator.language,
          preferred_languages: navigator.languages,
        }),
        schema: {
          user_agent: {
            type: 'keyword',
            _meta: { description: 'User agent of the browser.' },
          },
          preferred_language: {
            type: 'keyword',
            _meta: { description: 'Preferred language of the browser.' },
          },
          preferred_languages: {
            type: 'array',
            items: {
              type: 'keyword',
              _meta: { description: 'List of the preferred languages of the browser.' },
            },
          },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering browser info context', e)
    }
  }

  public registerUserIdContext({ userId, domain }: { userId: string; domain?: string }) {
    try {
      this.analytics.registerContextProvider({
        name: 'user-info',
        context$: of({ raw_user_id: userId, user_id: sha256(userId), domain }),
        schema: {
          user_id: {
            type: 'keyword',
            _meta: { description: 'Unique ID for every user hashed' },
          },
          raw_user_id: {
            type: 'keyword',
            _meta: { description: 'Unique ID for every user unhashed' },
          },
          domain: {
            type: 'keyword',
            _meta: {
              description: 'Org domain, could be aws, gcp azure and heroku, if missing is found',
              optional: true,
            },
          },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering user ID context', e)
    }
  }

  public registerOrgIdContext({ orgId }: { orgId: string }) {
    try {
      this.analytics.registerContextProvider({
        name: 'org-info',
        context$: of({ org_id: orgId }),
        schema: {
          org_id: {
            type: 'keyword',
            _meta: { description: 'Unique ID for every organization' },
          },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering org ID context', e)
    }
  }

  public updateUserDomainContext({ domain }: { domain: string }) {
    try {
      this.analytics.registerContextProvider({
        name: 'domain-info',
        context$: of({ domain }),
        schema: {
          domain: {
            type: 'keyword',
            _meta: { description: 'Org domain' },
          },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering domain context', e)
    }
  }

  public registerBillingContext(billingDetails: BillingDetails) {
    try {
      const billingContext = getBillingThirdPartyIntegrationContext(billingDetails)

      this.analytics.registerContextProvider({
        name: 'billing-info',
        context$: of(billingContext),
        schema: {
          billing_model: {
            type: 'keyword',
            _meta: {
              description: 'Billing model of the organization',
            },
          },
          billing_channel: {
            type: 'keyword',
            _meta: {
              description: 'Billing channel of the organization',
            },
          },
          billing_processing: {
            type: 'keyword',
            _meta: {
              description: 'Billing processing method of the organization',
              optional: true,
            },
          },
          billing_partner_selling: {
            type: 'keyword',
            _meta: {
              description: 'Billing partner selling type of the organization',
            },
          },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering billing context', e)
    }
  }

  public registerAnalyticsContextProvider({ location$ }: { location$: Observable<string> }) {
    try {
      this.analytics.registerContextProvider({
        name: 'cloud page url',
        context$: location$.pipe(map((location) => ({ page_url: location }))),
        schema: {
          page_url: { type: 'text', _meta: { description: 'The cloud page url' } },
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering analytics context provider', e)
    }
  }

  public persistFlagsContextProvider({ flags }: { flags: Record<string, any> }) {
    try {
      if (this.currentFlags) {
        this.updateFlagsContextProvider({ flags })
      } else {
        this.registerFlagsContextProvider({ flags })
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error persisting flags context provider', e)
    }
  }

  private registerFlagsContextProvider({ flags }: { flags: Record<string, any> }) {
    const properties = Object.keys(flags).reduce((acc, flag) => {
      acc[flag] = { type: 'pass_through', _meta: { description: flag } }
      return acc
    }, {})

    try {
      this.analytics.registerContextProvider({
        name: 'flags',
        context$: of({ flags }),
        schema: {
          flags: {
            properties,
          },
        },
      })
      // Store the current flags for comparison in future updates
      this.currentFlags = { ...flags }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error registering flags context provider', e)
    }
  }

  private updateFlagsContextProvider({ flags }: { flags: Record<string, any> }) {
    // Check if flags have actually changed
    if (this.currentFlags && isEqual(this.currentFlags, flags)) {
      return
    }

    const properties = Object.keys(flags).reduce((acc, flag) => {
      acc[flag] = { type: 'pass_through', _meta: { description: flag } }
      return acc
    }, {})

    try {
      // Remove the existing flags context provider
      try {
        this.analytics.removeContextProvider('flags')
      } catch (removeError) {
        // eslint-disable-next-line no-console
        console.debug('Context provider "flags" might not exist or failed to remove:', removeError)
      }

      // Register the updated flags context provider
      this.analytics.registerContextProvider({
        name: 'flags',
        context$: of({ flags }),
        schema: {
          flags: {
            properties,
          },
        },
      })

      // Update the stored flags for future comparisons
      this.currentFlags = { ...flags }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Error updating flags context provider', e)
    }
  }
}
